import React, { useState, useEffect, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Crown, Trophy, Clock, Sparkles, Medal, Flame, Plus, Calendar, Lightbulb } from 'lucide-react';
import LoginButton from './LoginButton';
import UserMenu from './UserMenu';
import DeckIdeaForm from './DeckIdeaForm';
import { DECK_INFO, DECK_TYPES } from '../mock/gameData';

// Get deterministic deck of the day based on current date
const getDeckOfTheDay = () => {
  const today = new Date().toISOString().split('T')[0]; // "2025-01-12"
  const hash = today.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const deckKeys = Object.keys(DECK_TYPES).filter(key => key !== 'RANDOM');
  const deckKey = deckKeys[hash % deckKeys.length];
  return DECK_TYPES[deckKey];
};

// Recently created decks (newest first)
const RECENTLY_CREATED_DECKS = [
  DECK_TYPES.TALLEST_BUILDINGS,
  DECK_TYPES.WHITNEY_SONGS,
  DECK_TYPES.ADELE_SONGS,
  DECK_TYPES.EMINEM_SONGS,
  DECK_TYPES.BILLIONAIRES,
];

// Twinkling stars background component
const TwinklingStars = () => {
  const stars = useMemo(() => {
    return Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      duration: `${2 + Math.random() * 4}s`,
      delay: `${Math.random() * 5}s`,
      color: ['white', 'pink', 'cyan'][Math.floor(Math.random() * 3)],
      size: Math.random() > 0.7 ? 4 : 2,
    }));
  }, []);

  const particles = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      duration: `${12 + Math.random() * 10}s`,
      delay: `${Math.random() * 15}s`,
      drift: `${-30 + Math.random() * 60}px`,
    }));
  }, []);

  return (
    <div className="stars-container">
      {stars.map((star) => (
        <div
          key={star.id}
          className={`star ${star.color}`}
          style={{
            left: star.left,
            top: star.top,
            width: `${star.size}px`,
            height: `${star.size}px`,
            '--duration': star.duration,
            '--delay': star.delay,
          }}
        />
      ))}
      {particles.map((particle) => (
        <div
          key={`p-${particle.id}`}
          className="particle"
          style={{
            left: particle.left,
            '--duration': particle.duration,
            '--delay': particle.delay,
            '--drift': particle.drift,
          }}
        />
      ))}
    </div>
  );
};

const GameSelector = ({ onGameSelect, user, onLogout, onViewLeaderboard, onViewStats, onNicknameChange }) => {
  const [recentDecks, setRecentDecks] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [popularDecks, setPopularDecks] = useState([]);
  const [loadingPopular, setLoadingPopular] = useState(true);
  const [topScores, setTopScores] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [showIdeaForm, setShowIdeaForm] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchRecentDecks = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/recently-played?limit=5`);
        if (response.ok) {
          const data = await response.json();
          setRecentDecks(data);
        }
      } catch (error) {
        console.log('Could not fetch recent decks:', error);
      } finally {
        setLoadingRecent(false);
      }
    };

    const fetchPopularDecks = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/popular-decks?limit=5`);
        if (response.ok) {
          const data = await response.json();
          setPopularDecks(data);
        }
      } catch (error) {
        console.log('Could not fetch popular decks:', error);
      } finally {
        setLoadingPopular(false);
      }
    };

    const fetchTopScores = async () => {
      try {
        // Fetch total points leaderboard instead of individual scores
        const response = await fetch(`${backendUrl}/api/leaderboard/total-points?limit=5`);
        if (response.ok) {
          const data = await response.json();
          setTopScores(data);
        }
      } catch (error) {
        console.log('Could not fetch leaderboard:', error);
      } finally {
        setLoadingLeaderboard(false);
      }
    };

    fetchRecentDecks();
    fetchPopularDecks();
    fetchTopScores();
  }, [backendUrl]);

  // Get rank icon
  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-gray-400 font-bold text-sm">{rank}</span>;
  };

  // Format relative time
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen synthwave-bg-dark retro-grid relative overflow-hidden p-8 scan-lines">
      {/* Twinkling Stars */}
      <TwinklingStars />
      
      {/* Synthwave Sun */}
      <div className="synthwave-sun"></div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Auth Header */}
        <div className="flex justify-end mb-4">
          {user ? (
            <UserMenu user={user} onLogout={onLogout} onViewLeaderboard={onViewLeaderboard} onViewStats={onViewStats} onNicknameChange={onNicknameChange} />
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={onViewLeaderboard}
                variant="outline"
                className="neon-border-cyan bg-transparent text-cyan-300 hover:bg-cyan-500/20 font-bold tracking-wide"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Leaderboard
              </Button>
              <LoginButton />
            </div>
          )}
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl font-bold mb-2 tracking-wider">
            <span className="neon-text-dual" style={{ fontFamily: "'Outrun future', sans-serif" }}>
              <Crown className="w-10 h-10 inline-block mr-2 text-yellow-400" style={{ filter: 'drop-shadow(0 0 10px #ffd93d)' }} />
              Pop Culture Wars
            </span>
          </h1>
          <div className="flex justify-center mb-4">
            <span className="px-3 py-1 text-xs font-bold uppercase tracking-widest bg-gradient-to-r from-cyan-500/20 to-pink-500/20 border border-cyan-400/50 rounded-full text-cyan-300" style={{ textShadow: '0 0 10px rgba(0, 255, 255, 0.5)' }}>
              Beta
            </span>
          </div>
          <p className="neon-text-cyan text-xl tracking-widest uppercase">Choose Your Game Mode</p>
          {user && (
            <p className="text-pink-400 mt-3 tracking-wide">
              Playing as: <span className="neon-text-pink font-semibold">{user.nickname || user.name}</span>
            </p>
          )}
        </div>

        {/* Game Options */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-lg mx-auto">
          {/* The Journey Mode */}
          <Card className="neon-border-pink bg-gradient-to-br from-yellow-900/60 via-orange-900/60 to-red-900/60 backdrop-blur-sm p-8 hover:scale-105 transition-all duration-300 cursor-pointer border-2 border-yellow-500/30">
            <div className="text-center space-y-4">
              <div className="flex justify-center text-7xl" style={{ filter: 'drop-shadow(0 0 20px #f59e0b)' }}>
                🗺️
              </div>
              <Button
                onClick={() => onGameSelect('journey')}
                className="w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black font-black py-6 text-xl uppercase tracking-widest transition-all duration-300 shadow-[0_0_20px_#f59e0b,0_0_40px_rgba(245,158,11,0.5)] hover:shadow-[0_0_30px_#f59e0b,0_0_60px_rgba(245,158,11,0.7)] hover:scale-105"
                data-testid="play-journey-btn"
              >
                ▶ The Journey
              </Button>
              <p className="text-yellow-200 text-lg">
                Pass every deck! 10 points advances you. How far can you go?
              </p>
            </div>
          </Card>

          {/* PCW - Which'ing Hour */}
          <Card className="neon-border-pink bg-gradient-to-br from-purple-900/80 to-pink-900/80 backdrop-blur-sm p-8 hover:scale-105 transition-all duration-300 cursor-pointer">
            <div className="text-center space-y-4">
              <div className="flex justify-center text-8xl neon-pulse" style={{ filter: 'drop-shadow(0 0 20px #ff2a6d)' }}>
                🧙‍♀️
              </div>
              <Button
                onClick={() => onGameSelect('whiching')}
                className="w-full bg-gradient-to-r from-cyan-400 via-cyan-500 to-teal-500 hover:from-cyan-300 hover:via-cyan-400 hover:to-teal-400 text-gray-900 font-black py-8 text-xl uppercase tracking-widest transition-all duration-300 shadow-[0_0_20px_#22d3ee,0_0_40px_rgba(34,211,238,0.5)] hover:shadow-[0_0_30px_#22d3ee,0_0_60px_rgba(34,211,238,0.7)] hover:scale-105"
                data-testid="play-whiching-hour-btn"
              >
                ▶ Which'ing Hour
              </Button>
              <p className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-1">
                Free Play • Choose Any Deck
              </p>
              <p className="text-cyan-200 text-lg">
                Pick the more popular choice! 3 strikes and you're out.
              </p>
            </div>
          </Card>

          {/* Daily Challenge */}
          {(() => {
            const deckOfTheDay = getDeckOfTheDay();
            const deck = DECK_INFO[deckOfTheDay];
            if (!deck) return null;
            return (
              <Card 
                className="neon-border-pink bg-gradient-to-r from-orange-600/40 via-red-600/40 to-pink-600/40 backdrop-blur-sm p-6 cursor-pointer hover:scale-[1.02] transition-all duration-300 border-2 border-orange-500/50"
                onClick={() => onGameSelect('daily_challenge', deckOfTheDay)}
                data-testid="daily-challenge"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">🏆</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Flame className="w-4 h-4 text-orange-400" />
                        <span className="text-xs text-orange-400 uppercase tracking-wider font-bold">Daily Challenge</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mt-1">{deck.name}</h3>
                      <p className="text-sm text-gray-400">Same cards for everyone • Compete for #1 today!</p>
                    </div>
                  </div>
                  <Button
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 text-white font-bold px-6"
                    data-testid="play-daily-challenge-btn"
                  >
                    Challenge →
                  </Button>
                </div>
              </Card>
            );
          })()}

          {/* Recently Created Decks */}
          <Card className="neon-border-cyan bg-black/40 backdrop-blur-sm p-6" data-testid="recently-created-section">
            <div className="space-y-4">
              <div className="flex items-center gap-2 neon-text-cyan">
                <Plus className="w-5 h-5" />
                <h3 className="text-lg font-semibold tracking-wide uppercase">Recently Created Decks</h3>
              </div>
              <div className="space-y-2">
                {RECENTLY_CREATED_DECKS.map((deckType, index) => {
                  const deck = DECK_INFO[deckType];
                  if (!deck) return null;
                  return (
                    <div
                      key={deckType}
                      onClick={() => onGameSelect('whiching', deckType)}
                      className="flex items-center justify-between bg-purple-900/30 border border-purple-500/30 rounded-lg px-4 py-3 hover:bg-purple-800/40 hover:border-cyan-500/50 transition-all duration-200 cursor-pointer group"
                      data-testid={`new-deck-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{deck.icon}</span>
                        <span className="text-cyan-100 font-medium group-hover:text-cyan-300 transition-colors">{deck.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-pink-400 uppercase tracking-wide font-semibold">New!</span>
                        <span className="text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity">Play →</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Total Points Leaderboard */}
          {!loadingLeaderboard && topScores.length > 0 && (
            <Card className="neon-border-pink bg-black/40 backdrop-blur-sm p-6" data-testid="leaderboard-preview-section">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 neon-text-pink">
                    <Trophy className="w-5 h-5" />
                    <h3 className="text-lg font-semibold tracking-wide uppercase">Total Points Leaders</h3>
                  </div>
                  <Button
                    onClick={onViewLeaderboard}
                    variant="ghost"
                    size="sm"
                    className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                  >
                    View All →
                  </Button>
                </div>
                <div className="space-y-2">
                  {topScores.map((entry, index) => (
                    <div
                      key={`${entry.nickname}-${index}`}
                      className={`flex items-center justify-between rounded-lg px-4 py-3 transition-all duration-200 ${
                        entry.rank === 1 
                          ? 'bg-gradient-to-r from-yellow-600/20 to-yellow-400/10 border border-yellow-500/30' 
                          : entry.rank === 2 
                          ? 'bg-gradient-to-r from-gray-500/20 to-gray-300/10 border border-gray-400/30'
                          : entry.rank === 3
                          ? 'bg-gradient-to-r from-amber-700/20 to-amber-500/10 border border-amber-500/30'
                          : 'bg-purple-900/30 border border-purple-500/30 hover:bg-purple-800/40'
                      }`}
                      data-testid={`leaderboard-entry-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        {getRankIcon(entry.rank)}
                        <span className="text-cyan-100 font-medium">{entry.nickname}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-green-400" title="Total Points (Best per Deck)">
                          <Trophy className="w-4 h-4" />
                          <span className="font-bold">{entry.total_points}</span>
                        </div>
                        <div className="flex items-center gap-1 text-purple-400" title="Decks Played">
                          <Sparkles className="w-4 h-4" />
                          <span>{entry.unique_decks} decks</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Recently Played Decks */}
          {!loadingRecent && recentDecks.length > 0 && (
            <Card className="neon-border-cyan bg-black/40 backdrop-blur-sm p-6" data-testid="recently-played-section">
              <div className="space-y-4">
                <div className="flex items-center gap-2 neon-text-cyan">
                  <Clock className="w-5 h-5" />
                  <h3 className="text-lg font-semibold tracking-wide uppercase">Recently Played Decks</h3>
                </div>
                <div className="space-y-2">
                  {recentDecks.map((deck, index) => (
                    <div
                      key={`${deck.deck_type}-${index}`}
                      className="flex items-center justify-between bg-purple-900/30 border border-purple-500/30 rounded-lg px-4 py-3 hover:bg-purple-800/40 hover:border-pink-500/50 transition-all duration-200"
                      data-testid={`recent-deck-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-4 h-4 text-pink-400" />
                        <span className="text-cyan-100 font-medium">{deck.deck_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-cyan-400/50 text-sm text-center pt-2 tracking-wide">
                  {Object.keys(DECK_INFO).length} decks available to play!
                </p>
              </div>
            </Card>
          )}

          {/* Most Popular Decks */}
          {!loadingPopular && popularDecks.length > 0 && (
            <Card className="neon-border-pink bg-black/40 backdrop-blur-sm p-6" data-testid="popular-decks-section">
              <div className="space-y-4">
                <div className="flex items-center gap-2 neon-text-pink">
                  <Flame className="w-5 h-5" />
                  <h3 className="text-lg font-semibold tracking-wide uppercase">Most Popular Decks</h3>
                </div>
                <div className="space-y-2">
                  {popularDecks.map((deck, index) => (
                    <div
                      key={`${deck.deck_type}-${index}`}
                      className="flex items-center justify-between bg-pink-900/30 border border-pink-500/30 rounded-lg px-4 py-3 hover:bg-pink-800/40 hover:border-cyan-500/50 transition-all duration-200"
                      data-testid={`popular-deck-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-pink-400 font-bold">#{index + 1}</span>
                        <span className="text-cyan-100 font-medium">{deck.deck_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Submit Deck Idea */}
          {showIdeaForm ? (
            <DeckIdeaForm onClose={() => setShowIdeaForm(false)} />
          ) : (
            <Card 
              className="neon-border-yellow bg-gradient-to-r from-yellow-900/20 to-orange-900/20 backdrop-blur-sm p-4 cursor-pointer hover:scale-[1.01] transition-all duration-200"
              onClick={() => setShowIdeaForm(true)}
              data-testid="submit-idea-button"
            >
              <div className="flex items-center justify-center gap-3">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-200 font-medium">Have a deck idea? Submit it here!</span>
              </div>
            </Card>
          )}
        </div>

        {/* Sign in prompt for non-authenticated users */}
        {!user && (
          <div className="text-center mt-8">
            <p className="text-purple-300">
              <LoginButton className="text-pink-400 hover:text-pink-300 underline" />
              {' '}to save your scores and compete on the leaderboard!
            </p>
          </div>
        )}
        
        {/* Decorative palm silhouettes */}
        <div className="hidden md:block absolute left-4 bottom-32 text-6xl opacity-20 select-none" style={{ filter: 'brightness(0) drop-shadow(0 0 5px #ff2a6d)' }}>
          🌴
        </div>
        <div className="hidden md:block absolute right-8 bottom-40 text-5xl opacity-20 select-none" style={{ filter: 'brightness(0) drop-shadow(0 0 5px #05d9e8)' }}>
          🌴
        </div>
      </div>
    </div>
  );
};

export default GameSelector;
